import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Avatar,
  Divider,
  CircularProgress,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { db, storage } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import TeacherRegistrationKey from './TeacherRegistrationKey';

const HOURLY_RATE_PER_STUDENT = 5; // €/h/élève
const HOURLY_RATE_CAP = 35; // €/h max

function Teachers() {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teacherCourses, setTeacherCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Récupérer tous les professeurs depuis les collections users et teachers
  useEffect(() => {
    const usersQuery = query(
      collection(db, 'users'),
      where('role', '==', 'Professeur')
    );

    const unsubscribe = onSnapshot(usersQuery, async (snapshot) => {
      setLoading(true);
      try {
        const updatedTeachers = await Promise.all(
          snapshot.docs.map(async (userDoc) => {
            const teacherDoc = await getDoc(doc(db, 'teachers', userDoc.id));
            return {
              id: userDoc.id,
              ...userDoc.data(),
              ...(teacherDoc.exists() ? teacherDoc.data() : {}),
            };
          })
        );
        setTeachers(updatedTeachers);
      } catch (error) {
        console.error('Erreur de chargement des professeurs :', error);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Récupérer les cours du prof sélectionné (nouveau schéma: teachers[] + dates[])
  useEffect(() => {
    if (!selectedTeacher) return;

    const fetchTeacherCourses = async () => {
      setCoursesLoading(true);
      try {
        const coursesSnapshot = await getDocs(collection(db, 'dance_courses'));
        const allCourses = coursesSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

        const relevantCourses = allCourses.filter((course) =>
          (course.teachers || []).some((t) => t.id === selectedTeacher.id)
        );

        setTeacherCourses(relevantCourses);
      } catch (error) {
        console.error('Erreur de chargement des cours :', error);
      } finally {
        setCoursesLoading(false);
      }
    };

    fetchTeacherCourses();
  }, [selectedTeacher]);

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacher(teacher);
    setTabValue(0);
  };

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  // Aplatit tous les cours du prof en séances individuelles (une entrée par date)
  const allSessions = useMemo(() => {
    const sessions = [];
    teacherCourses.forEach((course) => {
      (course.dates || []).forEach((dateObj, idx) => {
        sessions.push({
          key: `${course.id}_${idx}`,
          courseId: course.id,
          courseTitle: course.title,
          location: course.location,
          enrolledCount: (course.enrolledStudents || []).length,
          date: dateObj.date,
          startTime: dateObj.startTime,
          endTime: dateObj.endTime,
          duration: dateObj.duration || course.duration || 0, // minutes
        });
      });
    });
    return sessions.sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [teacherCourses]);

  const now = new Date();
  const upcomingSessions = allSessions.filter((s) => new Date(s.date) > now);
  const pastSessions = allSessions.filter((s) => new Date(s.date) <= now);

  if (loading) {
    return (
      <Box sx={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '80vh', flexDirection: 'column', gap: 3
      }}>
        <CircularProgress size={60} sx={{ color: '#7C3AED' }} />
        <Typography variant="h6" color="textSecondary">Chargement des données...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      {!selectedTeacher ? (
        <>
          <Box sx={{ mb: 3 }}>
            <TeacherRegistrationKey />
          </Box>
          <TeachersList teachers={teachers} navigate={navigate} onSelect={handleTeacherSelect} />
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
            <IconButton onClick={() => setSelectedTeacher(null)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1F2937' }}>
              {selectedTeacher.firstName} {selectedTeacher.lastName}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/users/${selectedTeacher.id}`)}
              sx={{ ml: 'auto' }}
            >
              Modifier
            </Button>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <InfoCard teacher={selectedTeacher} />
            </Grid>

            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2, borderRadius: '12px' }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mb: 2 }}
                >
                  <Tab label="Cours à venir" />
                  <Tab label="Heures & cours donnés" />
                  <Tab label="Documents" />
                </Tabs>

                {coursesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress sx={{ color: '#7C3AED' }} />
                  </Box>
                ) : (
                  <>
                    {tabValue === 0 && <UpcomingSessionsTab sessions={upcomingSessions} />}
                    {tabValue === 1 && <HoursTab sessions={pastSessions} />}
                    {tabValue === 2 && <DocumentsTab teacher={selectedTeacher} />}
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

// ─── Liste des profs ────────────────────────────────────────────────
function TeachersList({ teachers, navigate, onSelect }) {
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1F2937' }}>
          Gestion des Professeurs
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/users/new?role=Professeur')}
          sx={{ backgroundColor: '#4F46E5', '&:hover': { backgroundColor: '#4338CA' } }}
        >
          Ajouter un Professeur
        </Button>
      </Box>

      <Grid container spacing={3}>
        {teachers.map((teacher) => (
          <Grid item xs={12} sm={6} md={4} key={teacher.id}>
            <Card
              sx={{
                cursor: 'pointer', height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 20px rgba(0,0,0,0.15)' },
              }}
              onClick={() => onSelect(teacher)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ width: 56, height: 56, bgcolor: '#4F46E5', color: 'white' }}>
                    {teacher.firstName?.charAt(0)}{teacher.lastName?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {teacher.firstName} {teacher.lastName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">{teacher.email}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {teacher.phone || 'Téléphone non renseigné'}
                    </Typography>
                    {teacher.specialties && (
                      <Box sx={{ mt: 1 }}>
                        <Chip label={teacher.specialties} size="small" color="primary" variant="outlined" />
                      </Box>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {teachers.length === 0 && (
        <EmptyState icon={<CalendarIcon />} message="Aucun professeur pour le moment" />
      )}
    </>
  );
}

// ─── Carte infos ────────────────────────────────────────────────────
function InfoCard({ teacher }) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ width: 72, height: 72, bgcolor: '#4F46E5', color: 'white', fontSize: '2rem' }}>
            {teacher.firstName?.charAt(0)}{teacher.lastName?.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Informations</Typography>
            <Typography variant="body1">{teacher.email}</Typography>
            <Typography variant="body1">{teacher.phone || 'Téléphone non renseigné'}</Typography>
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body1">
          <strong>Spécialités:</strong> {teacher.specialties || 'Non spécifiées'}
        </Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          <strong>Disponibilités:</strong> {teacher.availability || 'Non spécifiées'}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ─── Cours à venir ──────────────────────────────────────────────────
function UpcomingSessionsTab({ sessions }) {
  const formatDate = (iso) => {
    try {
      return format(new Date(iso), "EEEE d MMMM yyyy 'à' HH:mm", { locale: fr });
    } catch {
      return 'Date invalide';
    }
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Cours</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Durée</TableCell>
            <TableCell>Lieu</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sessions.map((s) => (
            <TableRow key={s.key}>
              <TableCell>
                <Typography fontWeight="medium">{s.courseTitle}</Typography>
              </TableCell>
              <TableCell>{formatDate(s.date)}</TableCell>
              <TableCell>{s.duration} min</TableCell>
              <TableCell>{s.location?.name || 'Non spécifié'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {sessions.length === 0 && (
        <EmptyState icon={<CalendarIcon />} message="Aucun cours à venir pour ce professeur" />
      )}
    </TableContainer>
  );
}

// ─── Heures & cours donnés ──────────────────────────────────────────
function HoursTab({ sessions }) {
  const [period, setPeriod] = useState('all');

  const filteredSessions = useMemo(() => {
    const now = new Date();
    return sessions.filter((s) => {
      const d = new Date(s.date);
      if (period === 'month') {
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }
      if (period === 'lastMonth') {
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return d.getMonth() === lastMonth.getMonth() && d.getFullYear() === lastMonth.getFullYear();
      }
      if (period === 'year') {
        return d.getFullYear() === now.getFullYear();
      }
      return true; // 'all'
    });
  }, [sessions, period]);

  const totalMinutes = filteredSessions.reduce((sum, s) => sum + (s.duration || 0), 0);
  const totalHours = totalMinutes / 60;
  const totalCourses = filteredSessions.length;

  const estimatedPay = filteredSessions.reduce((sum, s) => {
    const rate = Math.min((s.enrolledCount || 0) * HOURLY_RATE_PER_STUDENT, HOURLY_RATE_CAP);
    return sum + rate * ((s.duration || 0) / 60);
  }, 0);

  const formatDate = (iso) => {
    try {
      return format(new Date(iso), 'dd/MM/yyyy HH:mm', { locale: fr });
    } catch {
      return 'Date invalide';
    }
  };

  return (
    <Box>
      <FormControl size="small" sx={{ mb: 3, minWidth: 200 }}>
        <InputLabel>Période</InputLabel>
        <Select value={period} label="Période" onChange={(e) => setPeriod(e.target.value)}>
          <MenuItem value="month">Ce mois-ci</MenuItem>
          <MenuItem value="lastMonth">Mois dernier</MenuItem>
          <MenuItem value="year">Cette année</MenuItem>
          <MenuItem value="all">Tout</MenuItem>
        </Select>
      </FormControl>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#F0F9FF', p: 2 }}>
            <Typography variant="body2" color="textSecondary">Cours donnés</Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#0EA5E9' }}>
              {totalCourses}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#F0FDF4', p: 2 }}>
            <Typography variant="body2" color="textSecondary">Heures totales</Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#10B981' }}>
              {totalHours.toFixed(1)} h
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#FEF3C7', p: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Rémunération estimée
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#D97706' }}>
              {estimatedPay.toFixed(2)} €
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Alert severity="info" sx={{ mb: 2 }}>
        Estimation basée sur 5€/h/élève inscrit au cours (plafond 35€/h). Elle utilise le nombre
        d'élèves inscrits au cours, pas une présence réelle séance par séance — à affiner plus tard.
      </Alert>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cours</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Durée</TableCell>
              <TableCell>Élèves inscrits</TableCell>
              <TableCell>Montant estimé</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSessions.slice().reverse().map((s) => {
              const rate = Math.min((s.enrolledCount || 0) * HOURLY_RATE_PER_STUDENT, HOURLY_RATE_CAP);
              const amount = rate * ((s.duration || 0) / 60);
              return (
                <TableRow key={s.key}>
                  <TableCell>{s.courseTitle}</TableCell>
                  <TableCell>{formatDate(s.date)}</TableCell>
                  <TableCell>{s.duration} min</TableCell>
                  <TableCell>{s.enrolledCount}</TableCell>
                  <TableCell>{amount.toFixed(2)} €</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {filteredSessions.length === 0 && (
          <EmptyState icon={<CalendarIcon />} message="Aucun cours donné sur cette période" />
        )}
      </TableContainer>
    </Box>
  );
}

// ─── Documents ──────────────────────────────────────────────────────
function DocumentsTab({ teacher }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDocuments = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, 'teachers', teacher.id, 'documents'));
        setDocuments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error('Erreur de chargement des documents:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchDocuments();
  }, [teacher.id]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError('');
    try {
      const safeName = file.name
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `teacher-documents/${teacher.id}/${Date.now()}_${safeName}`;
      const storageRef = ref(storage, storagePath);

      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);

      const docRef = await addDoc(collection(db, 'teachers', teacher.id, 'documents'), {
        name: file.name,
        url,
        storagePath,
        uploadedAt: serverTimestamp(),
        size: file.size,
      });

      setDocuments((prev) => [...prev, { id: docRef.id, name: file.name, url, storagePath, size: file.size }]);
    } catch (e) {
      console.error('Erreur upload document:', e);
      setError("Erreur lors de l'envoi du document.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (document) => {
    try {
      if (document.storagePath) {
        await deleteObject(ref(storage, document.storagePath));
      }
      await deleteDoc(doc(db, 'teachers', teacher.id, 'documents', document.id));
      setDocuments((prev) => prev.filter((d) => d.id !== document.id));
    } catch (e) {
      console.error('Erreur suppression document:', e);
      setError('Erreur lors de la suppression du document.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: '#7C3AED' }} />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Button
        variant="outlined"
        component="label"
        startIcon={<CloudUploadIcon />}
        disabled={uploading}
        sx={{ mb: 3 }}
      >
        {uploading ? 'Envoi en cours...' : 'Ajouter un document (contrat, etc.)'}
        <input hidden type="file" onChange={handleUpload} />
      </Button>

      <List>
        {documents.map((document) => (
          <ListItem
            key={document.id}
            secondaryAction={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton component="a" href={document.url} target="_blank" rel="noopener noreferrer">
                  <DownloadIcon />
                </IconButton>
                <IconButton color="error" onClick={() => handleDelete(document)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            }
            sx={{ border: '1px solid #E5E7EB', borderRadius: 2, mb: 1 }}
          >
            <ListItemIcon><DescriptionIcon /></ListItemIcon>
            <ListItemText
              primary={document.name}
              secondary={document.size ? `${(document.size / 1024).toFixed(0)} Ko` : ''}
            />
          </ListItem>
        ))}
      </List>

      {documents.length === 0 && (
        <EmptyState icon={<DescriptionIcon />} message="Aucun document pour ce professeur" />
      )}
    </Box>
  );
}

// ─── États vides ────────────────────────────────────────────────────
function EmptyState({ icon, message }) {
  return (
    <Box sx={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      height: 200, flexDirection: 'column', textAlign: 'center'
    }}>
      {React.cloneElement(icon, { sx: { fontSize: 48, color: '#9CA3AF', mb: 2 } })}
      <Typography variant="body1" color="textSecondary">{message}</Typography>
    </Box>
  );
}

export default Teachers;